//
// SchemaInspector
//
// Actions: commit/revert/save_as_new

import { observer } from 'mobx-react-lite'
import { useStore } from '../../stores'

import { Divider, Spacer, VView } from '../../appview'
import { Header, Icon, Menu, MenuItem, Property, Text } from '../../components'
import { MetadataPanel } from '../../panels'
import { getSchemaitemType } from '../../stores/data/utils'

const SchemaInspectorDebugInfo = observer(function SchemaInspectorDebugInfo({
    worksheet,
    item,
}) {
    const { app } = useStore()

    if (!item) return null

    return (
        <div className="DEBUG">
            <Header>{app.text('DEBUG info')}</Header>
            <Property label={app.text('gid')}>
                <Text>{item.gid}</Text>
            </Property>
            <Property label={app.text('original gid')}>
                <Text>{item.original}</Text>
            </Property>
            <Property label={app.text('layout gid')}>
                <Text>{item.layout}</Text>
            </Property>
        </div>
    )
})

export const SchemaInspector = observer(function SchemaInspector({
    tree,
    worksheet,
    viewkey,
}) {
    const { app, data, view, DEBUG } = useStore()

    const itemtype = getSchemaitemType(worksheet.item)

    const afterAction = result => {
        if (!worksheet.item || !tree) return
        const itemtype = worksheet.datatype
        tree.expandItem({ id: itemtype }).then(() => {
            const newitem = tree.findItemById(result[itemtype])
            tree.selectItem(newitem)
            view.schemaquery.refetch()
        })
    }
    const afterDeleteAction = result => {
        if (!worksheet.item || !tree) return
        const itemtype = worksheet.datatype
        tree.expandItem({ id: itemtype }).then(() => {
            const newitem = tree.findItemById(itemtype)
            tree.selectItem(newitem)
            view.schemaquery.refetch()
        })
    }

    const onChange = item => {
        item.commitIfModified()
            .then(result => selectModified(result))
            .catch(error => {})
    }

    const selectModified = result => {
        const item = data.fields.get(result[itemtype])
        if (item) {
            view.schematree.selectItem({ id: item.gid, item: item })
            view.schemaquery.refetch()
        }
        return result
    }

    let Inspector
    if (!worksheet.item) {
        Inspector = <Text>{app.text('Select a definition, class, or field.')}</Text>
    } else if (worksheet.item.is_new) {
        Inspector = (
            <>
                <Header className="cc-ok">
                    {app.text('New {itemtype}', { itemtype: worksheet.datatype })}
                </Header>
                <Text>
                    {app.text(
                        'This {itemtype} must be committed before it is available elsewhere.',
                        { itemtype: worksheet.datatype }
                    )}
                </Text>
                <Menu>
                    <MenuItem onClick={() => worksheet.item.commit().then(afterAction)}>
                        <Icon name="navigate" size={'text'} style={{ top: 0 }} />
                        {app.text('Commit')}
                    </MenuItem>
                    <MenuItem
                        onClick={() => worksheet.item.delete().then(afterDeleteAction)}
                    >
                        <Icon name="navigate" size={'text'} style={{ top: 0 }} />
                        {app.text('Delete')}
                    </MenuItem>
                </Menu>
            </>
        )
    } else if (worksheet.item.is_working_copy) {
        Inspector = (
            <>
                <Header className="cc-info">
                    {app.text('Modified {itemtype}', { itemtype: worksheet.datatype })}
                </Header>
                <Text>
                    {app.text(
                        'This {itemtype} must be committed before the changes are available elsewhere.',
                        { itemtype: worksheet.datatype }
                    )}
                </Text>
                <Menu>
                    <MenuItem onClick={() => worksheet.item.commit().then(afterAction)}>
                        <Icon name="navigate" size={'text'} style={{ top: 0 }} />
                        {app.text('Commit')}
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            worksheet.item.revert().then(afterAction)
                        }}
                    >
                        <Icon name="navigate" size={'text'} style={{ top: 0 }} />
                        {app.text('Revert changes')}
                    </MenuItem>
                    <MenuItem
                        onClick={() => worksheet.item.saveAsNew().then(afterAction)}
                    >
                        <Icon name="navigate" size={'text'} style={{ top: 0 }} />
                        {app.text('Save as new')}
                    </MenuItem>
                </Menu>
            </>
        )
    }

    const MetadataInspector = worksheet.item ? (
        <MetadataPanel
            language={worksheet.language}
            item={worksheet.item}
            viewkey={viewkey}
            onChange={onChange}
        />
    ) : undefined

    const DebugInfo = DEBUG ? (
        <>
            <Spacer size={5} />
            <Divider />
            <SchemaInspectorDebugInfo worksheet={worksheet} item={worksheet.item} />
        </>
    ) : undefined

    return (
        <VView className="panel properties">
            {Inspector}
            {Inspector && MetadataInspector ? (
                <>
                    <Spacer size={5} />
                    <Divider />
                </>
            ) : undefined}
            {MetadataInspector}
            {DebugInfo}
        </VView>
    )
})
